import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useShelters } from "@/hooks/useShelters";
import { useOwnedShelters } from "@/hooks/useOwnedShelters";
import { useClaimRequests } from "@/hooks/useClaimRequests";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Search, MapPin, Loader2, BadgeCheck, Send } from "lucide-react";
import { normalizeText } from "@/lib/geoText";
import { toast } from "sonner";
import { z } from "zod";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { useRequestTeamInvite } from "@/hooks/useTeamJoinRequests";

const formSchema = z.object({
  justification: z
    .string()
    .trim()
    .min(20, "Uzasadnienie musi mieć min. 20 znaków")
    .max(2000, "Uzasadnienie może mieć max 2000 znaków"),
});

const ClaimShelter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const prefillShelterId = searchParams.get("shelter");
  const { data: shelters, isLoading } = useShelters();
  const { data: ownedShelters } = useOwnedShelters(user?.id);
  const { data: claimRequests } = useClaimRequests(user?.id);

  // Multi-shelter wyłączony - jeśli user ma już schronisko lub pending wniosek,
  // odsyłamy z powrotem do pulpitu (bez możliwości zgłoszenia kolejnego).
  // Wyjątek: użytkownik jest członkiem schroniska bez opiekuna i chce sam zostać opiekunem.
  const ownsOnlyPrefilledOrphan =
    !!prefillShelterId &&
    !!ownedShelters &&
    ownedShelters.length === 1 &&
    ownedShelters[0].id === prefillShelterId &&
    ownedShelters[0].my_role === "member" &&
    ownedShelters[0].has_owner === false;

  const blockMultiShelter =
    !ownsOnlyPrefilledOrphan &&
    ((ownedShelters && ownedShelters.length > 0) ||
      (claimRequests && claimRequests.some((r) => r.status === "pending")));

  useEffect(() => {
    if (blockMultiShelter) {
      toast.info("Możesz zarządzać tylko jednym schroniskiem na konto");
      navigate("/panel", { replace: true });
    }
  }, [blockMultiShelter, navigate]);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<{ id: string; name: string; city: string; email?: string | null } | null>(null);
  const [justification, setJustification] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasOwner, setHasOwner] = useState<boolean | null>(null);
  const [joinMessage, setJoinMessage] = useState("");
  const [joinSent, setJoinSent] = useState(false);
  const requestInvite = useRequestTeamInvite();
  // Honeypot - boty wypełniają wszystkie pola; ludzie nie widzą tego inputu.
  const [hp, setHp] = useState("");
  // Czas montowania - zbyt szybkie wysłanie = bot
  const [mountedAt] = useState(() => Date.now());

  // Sprawdź czy wybrane schronisko ma już opiekuna profilu.
  // Jeśli tak, claim z innego konta i tak zostanie odrzucony przez DB trigger
  // - więc lepiej powiedzieć to jasno na froncie i pokierować na "poproś owner-a o zaproszenie".
  useEffect(() => {
    if (!selected) {
      setHasOwner(null);
      return;
    }
    let cancelled = false;
    setHasOwner(null);
    supabase
      .rpc("shelter_has_owner", { _shelter_id: selected.id })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("shelter_has_owner failed", error);
          setHasOwner(null);
          return;
        }
        setHasOwner(Boolean(data));
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);


  // Pre-fill schroniska z URL ?shelter=ID
  useEffect(() => {
    if (prefillShelterId && shelters && !selected) {
      const found = shelters.find((s) => s.id === prefillShelterId);
      if (found) {
        setSelected({ id: found.id, name: found.name, city: found.city, email: found.email });
      }
    }
  }, [prefillShelterId, shelters, selected]);

  const results = useMemo(() => {
    if (!shelters) return [];
    const q = normalizeText(query);
    if (!q || q.length < 2) return [];
    return shelters
      .filter((s) => normalizeText(s.name).includes(q) || normalizeText(s.city).includes(q))
      .slice(0, 8);
  }, [query, shelters]);

  const handleSubmit = async () => {
    if (!user || !selected) return;
    // Honeypot: jeśli ukryte pole wypełnione lub formularz wysłany w <2s -> udajemy sukces, ale nic nie robimy.
    if (hp.trim().length > 0 || Date.now() - mountedAt < 2000) {
      toast.success("Wniosek wysłany. Odezwiemy się po weryfikacji.");
      navigate("/panel", { replace: true });
      return;
    }
    const parsed = formSchema.safeParse({ justification });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      toast.error(first?.message ?? "Sprawdź formularz");
      return;
    }
    setSubmitting(true);
    try {
      const { data: inserted, error } = await supabase
        .from("shelter_claim_requests")
        .insert({
          user_id: user.id,
          shelter_id: selected.id,
          status: "pending",
          justification: parsed.data.justification,
          user_email: user.email,
        })
        .select("id")
        .single();
      if (error) {
        // Unique violation z partial index: jeden pending wniosek na użytkownika
        if (error.code === "23505") {
          toast.error("Masz już aktywny wniosek o dostęp do schroniska. Poczekaj na decyzję admina.");
          navigate("/panel", { replace: true });
          return;
        }
        throw error;
      }
      try {
        await supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "claim-request-received",
            recipientEmail: user.email,
            idempotencyKey: `claim-received-${user.id}-${selected.id}`,
            templateData: { shelterName: selected.name },
          },
        });
      } catch (mailErr) {
        console.warn("claim-received email failed", mailErr);
      }
      // Powiadom adminów (best-effort)
      if (inserted?.id) {
        try {
          await supabase.functions.invoke("notify-admin-claim", {
            body: { claim_id: inserted.id },
          });
        } catch (notifyErr) {
          console.warn("notify-admin-claim failed", notifyErr);
        }
      }
      toast.success("Wniosek wysłany. Odezwiemy się po weryfikacji.");
      queryClient.invalidateQueries({ queryKey: ["claim-requests", user.id] });
      navigate("/panel", { replace: true });
    } catch (e: any) {
      toast.error(e?.message ?? "Nie udało się wysłać wniosku");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Uzyskaj dostęp do profilu schroniska | Panel</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-2xl mx-auto space-y-2.5 sm:space-y-5">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/panel">
            <ArrowLeft className="h-4 w-4 mr-1" /> Wróć
          </Link>
        </Button>

        <Card>
          <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
            <CardTitle className="text-sm sm:text-base">Uzyskaj dostęp do profilu schroniska</CardTitle>
            <CardDescription>
              Po weryfikacji możesz samodzielnie aktualizować potrzeby, dane i informacje o schronisku.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-4">
            {!selected ? (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Nazwa schroniska lub miasto..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-9"
                    autoFocus
                  />
                </div>

                {isLoading && (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {query.length >= 2 && (
                  <ScrollArea className="max-h-72 border rounded-md">
                    {results.length > 0 ? (
                      <div className="p-1">
                        {results.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => setSelected({ id: s.id, name: s.name, city: s.city, email: s.email })}
                            className="w-full text-left px-3 py-2.5 rounded-md hover:bg-accent transition-colors flex items-start gap-2"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm flex items-center gap-1.5">
                                {s.name}
                                {s.organization_verified && (
                                  <BadgeCheck className="h-3.5 w-3.5 text-primary shrink-0" />
                                )}
                              </div>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                <MapPin className="h-3 w-3" /> {s.city}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-6">
                        Nie znaleziono schroniska. Jeśli go nie ma na liście,{" "}
                        <Link to="/zglos" className="text-primary underline">
                          zgłoś nowe
                        </Link>
                        .
                      </p>
                    )}
                  </ScrollArea>
                )}
              </>
            ) : (
              <>
                <div className="rounded-md border p-3 bg-muted/40 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-sm">{selected.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3" /> {selected.city}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                    Zmień
                  </Button>
                </div>

                {hasOwner === true ? (
                  <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800 p-3 space-y-2.5">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-orange-900 dark:text-orange-200">
                        To schronisko ma już opiekuna profilu
                      </p>
                      <p className="text-xs text-orange-900/90 dark:text-orange-200/90">
                        Możesz poprosić obecnego opiekuna o dołączenie do zespołu - Twoja prośba pojawi się u niego w panelu „Zespół".
                      </p>
                      <p className="text-xs text-orange-900/90 dark:text-orange-200/90">
                        Coś nie gra (np. opiekun już nie współpracuje ze schroniskiem)? Napisz do nas przez czat w prawym dolnym rogu.
                      </p>
                    </div>
                    {joinSent ? (
                      <div className="rounded-md bg-background/60 border border-orange-200 dark:border-orange-800 p-2.5 text-xs text-orange-900 dark:text-orange-200">
                        ✅ Prośba wysłana. Opiekun schroniska zobaczy ją w panelu „Zespół" i zdecyduje, czy dodać Cię do zespołu. Jeśli zaakceptuje, dostaniesz na maila link aktywacyjny.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label htmlFor="join-msg" className="text-xs text-orange-900 dark:text-orange-200">
                          Wiadomość do opiekuna (kim jesteś, dlaczego chcesz pomóc)
                        </Label>
                        <Textarea
                          id="join-msg"
                          rows={3}
                          maxLength={1000}
                          placeholder="Np. Jestem wolontariuszką, pomagam przy karmieniu i chciałabym aktualizować potrzeby na profilu."
                          value={joinMessage}
                          onChange={(e) => setJoinMessage(e.target.value)}
                          className="bg-background/80"
                        />
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] text-orange-900/70 dark:text-orange-200/70">
                            {joinMessage.trim().length}/1000 (min. 20)
                          </span>
                          <Button
                            size="sm"
                            disabled={requestInvite.isPending || joinMessage.trim().length < 20}
                            onClick={async () => {
                              try {
                                await requestInvite.mutateAsync({
                                  shelterId: selected.id,
                                  message: joinMessage.trim(),
                                });
                                setJoinSent(true);
                                toast.success("Prośba wysłana do opiekuna");
                              } catch (e: any) {
                                toast.error(e?.message ?? "Nie udało się wysłać prośby");
                              }
                            }}
                          >
                            {requestInvite.isPending ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4 mr-2" />
                            )}
                            Wyślij prośbę
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="pt-1">
                      <Button asChild variant="outline" size="sm" className="h-8">
                        <Link to="/panel">Wróć do panelu</Link>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="just">Uzasadnij krótko, dlaczego prosisz o dostęp *</Label>
                      <Textarea
                        id="just"
                        rows={3}
                        placeholder="Np. jestem wolontariuszem schroniska lub administruję jego profilem na Facebooku."
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        maxLength={2000}
                        className="focus-visible:ring-1 focus-visible:ring-primary/40 focus-visible:ring-offset-0"
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Weryfikacja zwykle trwa do 24-48h.</p>
                        <p className="text-xs text-muted-foreground">{justification.length}/2000</p>
                      </div>
                    </div>

                    <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
                      <p className="text-xs font-medium text-foreground/80 mb-1">Co zyskujesz:</p>
                      {[
                        "aktualizacja potrzeb",
                        "oznaczanie pilnych zbiórek",
                        "edycja danych kontaktowych",
                        "status zweryfikowanego schroniska",
                      ].map((b) => (
                        <div key={b} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="text-primary">✅</span> {b}
                        </div>
                      ))}
                    </div>

                    <p className="text-xs text-muted-foreground">
                      W razie potrzeby możemy skontaktować się mailowo lub bezpośrednio ze schroniskiem, aby potwierdzić zgłoszenie.
                    </p>

                    {/* Honeypot - ukryte przed użytkownikami, widoczne dla botów */}
                    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                      <label htmlFor="website_url">Strona www (nie wypełniaj):</label>
                      <input
                        id="website_url"
                        name="website_url"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={hp}
                        onChange={(e) => setHp(e.target.value)}
                      />
                    </div>

                    <Button onClick={handleSubmit} disabled={submitting} className="w-full">
                      {submitting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Wyślij wniosek
                    </Button>
                  </>
                )}
                <PrivacyNotice />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ClaimShelter;
